import React, { useState, useMemo, useEffect } from 'react';

import { styled } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';

import {
  Button,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Container,
  InputAdornment,
  TextField,
  Fade,
  Chip,
  Skeleton,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

// Icônes
import {
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
} from '@mui/icons-material';

import { useWishlist } from '../context/WishlistContext';
import { useProducts, Product } from '../context/ProductsContext';

import PromoCountdownBanner from '../components/PromoCountdownBanner';

// Composants stylisés
const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  color: 'white',
  padding: theme.spacing(10, 2),
  textAlign: 'center',
  marginBottom: theme.spacing(4),
  borderRadius: 0,
  overflow: 'hidden',
  background: 'linear-gradient(90deg, rgb(21,21,21) 0%, rgb(29,29,29) 100%)',
}));

const getFinalPrice = (product: Product) =>
  product.discount && product.discount > 0
    ? product.price * (1 - product.discount / 100)
    : product.price;

const isLowStock = (product: Product, threshold: number) =>
  product.stock != null && product.stock > 0 && product.stock <= threshold;

// Nouvelle implémentation simplifiée de la page produits
const Products = () => {
  const theme = useTheme();
  const location = useLocation();

  const defaultHeartColor = theme.palette.getContrastText(theme.palette.background.paper);
  const { isInWishlist, toggle } = useWishlist();

  const { products, settings } = useProducts();
  const [heartBgMap, setHeartBgMap] = useState<Record<number, boolean>>({});

  const handleImageLoad =
    (productId: number) => (event: React.SyntheticEvent<HTMLImageElement>) => {
      try {
        const img = event.currentTarget as HTMLImageElement;
        const { naturalWidth, naturalHeight } = img;

        if (!naturalWidth || !naturalHeight) {
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = naturalWidth;
        canvas.height = naturalHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          return;
        }

        ctx.drawImage(img, 0, 0, naturalWidth, naturalHeight);

        const sampleSize = Math.max(Math.floor(naturalWidth * 0.08), 16);
        const margin = Math.max(Math.floor(sampleSize * 0.3), 4);

        const startX = Math.max(naturalWidth - sampleSize - margin, 0);
        const startY = margin;
        const width = Math.min(sampleSize, naturalWidth - startX);
        const height = Math.min(sampleSize, naturalHeight - startY);

        const imageData = ctx.getImageData(startX, startY, width, height);
        const { data } = imageData;

        let totalLuminance = 0;
        const pixelCount = data.length / 4;

        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
          totalLuminance += luminance;
        }

        const avgLuminance = totalLuminance / pixelCount;
        const isDark = avgLuminance < 140;

        setHeartBgMap((prev) => ({
          ...prev,
          [productId]: isDark,
        }));
      } catch (error) {
        console.error(
          "Erreur lors de la détection de luminosité de l'image produit :",
          error
        );
      }
    };

  const categories = useMemo(
    () => [...new Set(products.map((product) => product.category))],
    [products]
  );
  const allSizes = useMemo(
    () => [...new Set(products.flatMap((product) => product.sizes || []))],
    [products]
  );

  const [searchTerm, setSearchTerm] = useState('');

  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sizeFilter, setSizeFilter] = useState<string>('all');
  const [priceRange, setPriceRange] = useState<number[]>([0, 1000]);
  const [sortBy, setSortBy] = useState<'default' | 'price-asc' | 'price-desc' | 'rating-desc'>(
    'default'
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    setSearchTerm(q);
  }, [location.search]);

  const filteredProducts = products.filter((product) => {
    const matchesCategory =
      categoryFilter === 'all' || product.category === categoryFilter;

    const normalizedSearch = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !normalizedSearch ||
      product.name.toLowerCase().includes(normalizedSearch) ||
      (product.category || '').toLowerCase().includes(normalizedSearch);

    const matchesPrice =
      product.price >= priceRange[0] && product.price <= priceRange[1];

    const matchesSize =
      sizeFilter === 'all' || (product.sizes && product.sizes.includes(sizeFilter));

    return matchesCategory && matchesSearch && matchesPrice && matchesSize;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-asc':
        return a.price - b.price;
      case 'price-desc':
        return b.price - a.price;
      case 'rating-desc':
        return b.rating - a.rating;
      default:
        return 0;
    }
  });

  const scrollToCollection = () => {
    const section = document.getElementById('collection-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const resetFilters = () => {
    setCategoryFilter('all');
    setSearchTerm('');
    setSizeFilter('all');
    setPriceRange([0, 1000]);
    setSortBy('default');
  };

  const renderRatingSimple = (rating: number) => (
    <Box display="flex" alignItems="center" mt={1}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Box key={star} color={star <= Math.round(rating) ? 'gold' : 'grey.400'}>
          {star <= Math.round(rating) ? (
            <StarIcon fontSize="small" />
          ) : (
            <StarBorderIcon fontSize="small" />
          )}
        </Box>
      ))}
      <Typography variant="caption" color="text.secondary" ml={1}>
        ({rating.toFixed(1)})
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', pt: 10 }}>
      <PromoCountdownBanner />
      <HeroSection>
        <Fade in timeout={1000}>
          <Box>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
              }}
            >
              Découvrez Notre Collection Unique 2026
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                borderRadius: '50px',
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '1.1rem',
              }}
              onClick={scrollToCollection}
            >
              Découvrir
            </Button>
          </Box>
        </Fade>
      </HeroSection>

      <Container id="collection-section" maxWidth="lg" sx={{ py: 6 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            justifyContent: 'space-between',
            mb: 4,
            gap: 2,
          }}
        >
          <Typography variant="h4" component="h2" sx={{ fontWeight: 700 }}>
            Nos produits
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              placeholder="Rechercher..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '50px',
                  bgcolor: 'background.paper',
                  minWidth: '250px',
                },
              }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="category-label">Catégorie</InputLabel>
              <Select
                labelId="category-label"
                value={categoryFilter}
                label="Catégorie"
                onChange={(e) => setCategoryFilter(e.target.value as string)}
                sx={{ borderRadius: '50px' }}
              >
                <MenuItem value="all">Toutes les catégories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="size-label">Taille</InputLabel>
              <Select
                labelId="size-label"
                value={sizeFilter}
                label="Taille"
                onChange={(e) => setSizeFilter(e.target.value as string)}
                sx={{ borderRadius: '50px' }}
              >
                <MenuItem value="all">Toutes les tailles</MenuItem>
                {allSizes.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="sort-label">Trier par</InputLabel>
              <Select
                labelId="sort-label"
                value={sortBy}
                label="Trier par"
                onChange={(e) =>
                  setSortBy(
                    e.target.value as 'default' | 'price-asc' | 'price-desc' | 'rating-desc'
                  )
                }
                sx={{ borderRadius: '50px' }}
              >
                <MenuItem value="default">Pertinence</MenuItem>
                <MenuItem value="price-asc">Prix croissant</MenuItem>
                <MenuItem value="price-desc">Prix décroissant</MenuItem>
                <MenuItem value="rating-desc">Mieux notés</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              sx={{ borderRadius: '50px' }}
              onClick={resetFilters}
            >
              Réinitialiser
            </Button>
          </Box>
        </Box>

        {filteredProducts.length === 0 ? (
          products.length === 0 ? (
            <Box sx={{ py: 8 }}>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ mb: 3, textAlign: 'center' }}
              >
                Chargement des produits...
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(3, 1fr)',
                  },
                  gap: 4,
                }}
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <Card key={index}>
                    <Skeleton variant="rectangular" height={260} />
                    <CardContent>
                      <Skeleton width="70%" />
                      <Skeleton width="40%" />
                      <Skeleton width="60%" />
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                Aucun produit ne correspond à votre recherche.
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                sx={{ mt: 2 }}
                onClick={resetFilters}
              >
                Réinitialiser les filtres
              </Button>
            </Box>
          )
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
              gap: 4,
            }}
          >
            {sortedProducts.map((product) => {
              const isHeartOnDarkBg = heartBgMap[product.id];
              const heartColor =
                typeof isHeartOnDarkBg === 'boolean'
                  ? isHeartOnDarkBg
                    ? theme.palette.common.white
                    : theme.palette.common.black
                  : defaultHeartColor;
              const inWishlist = isInWishlist(product.id);

              return (
                <Card
                  key={product.id}
                  component={Link}
                  to={`/products/${product.id}`}
                  sx={{ textDecoration: 'none', color: 'inherit', position: 'relative' }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 2,
                    }}
                  >
                    <IconButton
                      size="small"
                      color="default"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggle(product.id);
                      }}
                      sx={{
                        bgcolor: inWishlist
                          ? theme.palette.error.main
                          : 'rgba(255,255,255,0.85)',
                        '&:hover': {
                          bgcolor: inWishlist
                            ? theme.palette.error.dark
                            : 'rgba(0,0,0,0.08)',
                        },
                        boxShadow: inWishlist ? 1 : 0,
                        transition:
                          'background-color 0.2s ease, box-shadow 0.2s ease',
                      }}
                    >
                      {inWishlist ? (
                        <FavoriteIcon
                          sx={{
                            color: theme.palette.common.white,
                          }}
                        />
                      ) : (
                        <FavoriteBorderIcon
                          sx={{
                            color: heartColor,
                          }}
                        />
                      )}
                    </IconButton>
                  </Box>
                  <CardMedia
                    component="img"
                    height="260"
                    image={product.image}
                    alt={product.name}
                    loading="lazy"
                    sx={{ objectFit: 'cover' }}
                    onLoad={handleImageLoad(product.id)}
                  />

                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      component="h3"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {product.name}
                    </Typography>
                    {product.reviews > 0 && product.rating > 0 &&
                      renderRatingSimple(product.rating)}
                    <Box sx={{ mt: 1 }}>
                      {product.discount && product.discount > 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textDecoration: 'line-through' }}
                        >
                          {product.price.toFixed(2)} €
                        </Typography>
                      )}
                      <Typography variant="h6" color="primary">
                        {getFinalPrice(product).toFixed(2)} €
                      </Typography>
                      {isLowStock(product, settings.lowStockThreshold) && (
                        <Chip
                          label="Stock faible"
                          color="warning"
                          size="small"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  </CardContent>
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="outlined"
                      color="primary"
                      fullWidth
                    >
                      Voir le produit
                    </Button>
                  </CardActions>
                </Card>
              );
            })}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Products;